import { getCurrentInstance, ComponentInternalInstance, ref, Ref, watch, nextTick } from '@vue/composition-api';
import dayjs from 'dayjs';

import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { formatSize, getSpaceDirectory } from '@/renderer/scripts/utils/tools';

type Handler = {
  show: Ref<boolean>;
  methods: {
    openDetail: () => void;
    getLinkDetailData: (item: SMH.GetShareLinkData) => void;
    reset: () => void;
  };
  detailData: Ref<SMH.GetSharedLinkItemData | {}>;
  detailItem: Ref;
  loading: Ref<boolean>;
};

export const DetailType = {
  space: i18n_t('空间位置'),
  path: i18n_t('文件路径'),
  fileType: i18n_t('类型'),
  size: i18n_t('大小'),
  creationTime: i18n_t('Special.创建时间'),
  expireTime: i18n_t('Special.过期时间'),
};

export const BaseDetailType = {
  name: i18n_t('名称'),
  creationPerson: i18n_t('创建人'),
};

function formatSizeString(itemSize?: string) {
  const { size, unit } = formatSize(itemSize as string);
  return `${size} ${unit}`;
}

export default function handleRowDetail(): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const show = ref(false);
  const loading = ref(false);
  const detailData = ref<SMH.getDirectoryOrFileInfoData | {}>({});
  const detailItem = ref({
    type: 'file',
    fileType: '',
    size: '-',
    creationTime: '',
    expireTime: '',
    path: '',
    space: '',
    name: '',
    creationPerson: '',
  });
  const methods = {
    modifyData(detailData: any, selectedItem: SMH.GetShareLinkData) {
      if (!detailData.modificationTime) {
        // 没有修改时间，就让过期时间等于修改时间
        detailData.modificationTime = detailData.expireTime;
      }
      const sizeValue = formatSizeString(detailData.size);
      const spaceStr = getSpaceDirectory(detailData);
      const pathStr = i18n_t('全部/{slot1}', {
        slot1: detailData.path?.slice(0, detailData.path?.length - 1).join('/'),
      });
      nextTick(() => {
        detailItem.value = {
          type: selectedItem.type ? selectedItem.type : 'file',
          fileType: selectedItem.type !== 'multi-file' && selectedItem.fileType ? selectedItem.fileType : '',
          size: selectedItem.type !== 'dir' && selectedItem.type !== 'multi-file' ? sizeValue : '-',
          creationTime: dayjs(detailData.creationTime).format('YYYY-MM-DD HH:mm:ss'),
          expireTime: selectedItem.isPermanent
            ? i18n_t('永久有效')
            : dayjs(detailData.modificationTime).format('YYYY-MM-DD HH:mm:ss'),
          path: pathStr,
          space: spaceStr,
          name: detailData.name,
          creationPerson: detailData.userNickname,
        };
      });
    },
    openDetail(): void {
      show.value = true;
    },
    async getLinkDetailData(item: SMH.GetShareLinkData) {
      try {
        loading.value = true;
        const { data } = await smh.userAPI.getSharedLinkItem({ shareId: item.id });
        detailData.value = data;
        methods.modifyData(data, item);
        loading.value = false;
      } catch (error) {
        loading.value = false;
        tips.error(error as MsgError);
      }
    },
    reset() {
      detailData.value = {};
      show.value = false;
    },
  };

  watch(
    () => vm.$route,
    () => {
      methods.reset();
    },
  );

  return { show, methods, detailData, detailItem, loading };
}
